import type { AgentSelectResolution } from '~/components/Filters/AgentSelect';
import type { SelectedAgentSummaryResult } from '~/utils/selectedAgentSummary';
import type { AgentSearchResult } from '~~/core/entities/AgentSearch';
import type { AgentsRepository } from '~~/core/ports/repositories/AgentsRepository';
import type { MaybeRefOrGetter } from 'vue';

import { useLogger } from '~/composables/useLogger';
import { loadSelectedAgentSummary } from '~/utils/selectedAgentSummary';
import { nuxtAgentsRepository } from '~~/adapters/repositories/nuxt/nuxtAgentsRepository';
import { computed, onScopeDispose, ref, toValue, watch } from 'vue';

interface UseSelectedAgentSummaryOptions {
	isEnabled: boolean;
	agentSlug: MaybeRefOrGetter<string | undefined>;
	repository?: Pick<AgentsRepository, 'getOne'>;
	onAgentNotFound?: (slug: string) => void;
}

export function useSelectedAgentSummary({
	isEnabled,
	agentSlug,
	repository = nuxtAgentsRepository,
	onAgentNotFound
}: UseSelectedAgentSummaryOptions) {
	const route = useRoute();
	const router = useRouter();
	const isNuxtReady = ref(false);
	let disposed = false;
	onScopeDispose(() => {
		disposed = true;
	});
	const selectedAgentSlug = computed(() =>
		isEnabled ? toValue(agentSlug)?.trim() || '' : ''
	);
	const logger = useLogger('selectedAgentSummary');
	let activeRequest:
		| { slug: string; signal: AbortSignal; logged: boolean }
		| undefined;
	// Invalidate before Nuxt's synchronous reactive-key watcher starts a new lookup.
	watch(selectedAgentSlug, () => (activeRequest = undefined), {
		flush: 'sync'
	});
	const summaryKey = computed(
		() =>
			`properties-listing-agent-summary-${selectedAgentSlug.value || 'none'}`
	);
	const { data, status, error, execute } =
		useAsyncData<SelectedAgentSummaryResult>(
			summaryKey,
			(_nuxtApp, { signal }) => {
				// Reactive keys can execute before hydration even with immediate: false.
				if (disposed || !isNuxtReady.value) return Promise.resolve(null);

				activeRequest = {
					slug: selectedAgentSlug.value,
					signal,
					logged: false
				};
				return loadSelectedAgentSummary({
					isEnabled,
					agentSlug: selectedAgentSlug.value,
					repository,
					signal
				});
			},
			{
				deep: false,
				dedupe: 'cancel',
				default: () => null,
				immediate: false,
				server: false,
				timeout: 8000
			}
		);

	watch([status, error], ([requestStatus, requestError]) => {
		const request = activeRequest;
		if (
			requestStatus !== 'error' ||
			!requestError ||
			!request?.slug ||
			request.slug !== selectedAgentSlug.value ||
			request.logged ||
			(request.signal.aborted &&
				request.signal.reason?.name !== 'TimeoutError') ||
			requestError.name === 'AbortError' ||
			(requestError.cause instanceof Error &&
				requestError.cause.name === 'AbortError')
		) {
			return;
		}

		request.logged = true;
		logger.warn('Failed to load selected agent summary', {
			agent_slug: request.slug,
			error: requestError
		});
	});

	// Keep the initial client tree equal to SSR until lazy listing components hydrate.
	onNuxtReady(() => {
		if (disposed) return;

		isNuxtReady.value = true;
		if (selectedAgentSlug.value) void execute();
	});

	const selectedAgent = computed<AgentSearchResult | undefined>(() => {
		const result = data.value;
		return result?.status === 'found' &&
			result.slug === selectedAgentSlug.value &&
			result.agent.slug === selectedAgentSlug.value
			? result.agent
			: undefined;
	});
	const isFetchingSelectedAgent = computed(
		() =>
			Boolean(selectedAgentSlug.value) &&
			(!isNuxtReady.value || ['idle', 'pending'].includes(status.value))
	);
	const selectedAgentResolution = computed<AgentSelectResolution | undefined>(
		() => {
			const slug = selectedAgentSlug.value;
			if (!slug) return undefined;

			if (isFetchingSelectedAgent.value) {
				return { slug, status: 'loading' };
			}

			return selectedAgent.value ? undefined : { slug, status: 'unavailable' };
		}
	);

	watch([data, status], async ([result, requestStatus]) => {
		if (
			requestStatus !== 'success' ||
			result?.status !== 'not-found' ||
			result.slug !== selectedAgentSlug.value
		) {
			return;
		}

		const queryAgentSlug = Array.isArray(route.query.agentSlug)
			? route.query.agentSlug[0]
			: route.query.agentSlug;
		if (queryAgentSlug?.trim() !== result.slug) return;

		const query = { ...route.query };
		delete query.agentSlug;
		delete query.page;
		// The listing manager reacts to this URL change and refreshes all results.
		onAgentNotFound?.(result.slug);
		try {
			const failure = await router.replace({
				path: route.path,
				query,
				hash: route.hash
			});
			if (failure) {
				logger.warn('Failed to remove missing agent from URL', {
					agent_slug: result.slug,
					error: failure
				});
			}
		} catch (error) {
			logger.warn('Failed to remove missing agent from URL', {
				agent_slug: result.slug,
				error
			});
		}
	});

	async function retrySelectedAgentSummary() {
		if (
			disposed ||
			!isNuxtReady.value ||
			!selectedAgentSlug.value ||
			status.value === 'pending'
		) {
			return;
		}

		await execute();
	}

	return {
		selectedAgent,
		isFetchingSelectedAgent,
		selectedAgentResolution,
		retrySelectedAgentSummary
	};
}
